import { mediaProjectsApi } from '@features/mediaProjectsApi/api'
import { WorkflowSessionDetail } from '@features/sessions/types'
import { OrgId } from '@features/organisations/types'
import { WorkflowSessionId } from '@customTypes/cloudmix/workflow'
import {
  ListOrgSessionRequest,
  listOrgSessionRequestSchema,
  ListSessionsRequest,
  listSessionsRequestSchema,
  ListSessionsResponse,
  DEFAULT_LIST_ORDER,
} from '@customTypes/cloudmix/sessions'
import { ProjectId } from '@customTypes/cloudmix/projects'
import {
  DEFAULT_INFINITE_PAGE_SIZE,
  DEFAULT_INFINITE_MAX_PAGES,
  PaginationRequest,
  paginationRequestSchema,
} from '@customTypes/cloudmix/api'
import { SessionStatus } from '@customTypes/cloudmix/sessions/status'

const baseUri = `${import.meta.env.VITE_MEDIA_LIBRARY_API_URI}`

const sessionListTags = (result?: ListSessionsResponse) =>
  result
    ? [
        ...result.items.map((val) => ({
          type: 'WorkflowSession' as const,
          id: val.workflowSessionId,
        })),
        { type: 'WorkflowSession' as const, id: 'PARTIAL-LIST' },
      ]
    : [{ type: 'WorkflowSession' as const, id: 'PARTIAL-LIST' }]

export const workflowSessionApi = mediaProjectsApi.injectEndpoints({
  endpoints: (build) => ({
    createProjectSessions: build.mutation<
      WorkflowSessionDetail,
      { projectId: ProjectId }
    >({
      query: ({ projectId }) => ({
        url: `${baseUri}/sessions`,
        method: `POST`,
        body: {
          projectId,
        },
      }),
      invalidatesTags: () => [
        { type: 'WorkflowSession', id: 'PARTIAL-LIST' },
        { type: 'Project', id: 'PARTIAL-LIST' },
      ],
    }),
    getWorkflowSession: build.query<
      WorkflowSessionDetail,
      { workflowSessionId: WorkflowSessionId }
    >({
      query: ({ workflowSessionId }) => ({
        url: `${baseUri}/sessions/${workflowSessionId}`,
        method: `GET`,
      }),
      keepUnusedDataFor: 10,
      providesTags: (_result, _error, arg) => [
        { type: 'WorkflowSession', id: arg.workflowSessionId },
      ],
    }),
    listWorkflowSession: build.query<ListSessionsResponse, ListSessionsRequest>(
      {
        query: ({ pageSize, pageIndex, parentResourceId, status, orderBy }) => {
          const validatedParams = listSessionsRequestSchema.safeParse({
            pageIndex,
            pageSize,
            parentResourceId,
            status,
            orderBy,
          })

          return {
            url: `${baseUri}/sessions`,
            method: 'GET',
            ...(validatedParams.success && {
              params: validatedParams.data,
            }),
          }
        },
        providesTags: sessionListTags,
      }
    ),
    listOrgWorkflowSessions: build.query<
      ListSessionsResponse,
      Partial<ListOrgSessionRequest>
    >({
      query: ({ pageIndex, pageSize, orderBy, filterBy, status }) => {
        const validatedParams = listOrgSessionRequestSchema.safeParse({
          pageIndex,
          pageSize,
          orderBy,
          filterBy,
          status,
        })

        return {
          url: `${baseUri}/sessions`,
          method: 'GET',
          ...(validatedParams.success && {
            params: validatedParams.data,
          }),
        }
      },
      providesTags: sessionListTags,
    }),
    stopWorkflowSession: build.mutation<
      void,
      { workflowSessionId: WorkflowSessionId; orgId?: OrgId }
    >({
      query: ({ workflowSessionId, orgId }) => {
        // This has to be part of the URL for baseQueryWithOrgId(). params helper is added after baseQueryWithOrgId()
        const query = orgId ? `?orgId=${orgId}` : ''
        return {
          url: `${baseUri}/sessions/${workflowSessionId}${query}`,
          method: 'DELETE',
        }
      },
      invalidatesTags: (_result, _error, arg) => [
        { type: 'WorkflowSession', id: 'PARTIAL-LIST' },
        { type: 'WorkflowSession', id: arg.workflowSessionId },
        { type: 'Project', id: 'PARTIAL-LIST' },
      ],
    }),
    getAllSessions: build.infiniteQuery<
      ListSessionsResponse,
      { search?: string; status?: SessionStatus } | null,
      PaginationRequest
    >({
      infiniteQueryOptions: {
        initialPageParam: {
          pageIndex: 0,
          pageSize: DEFAULT_INFINITE_PAGE_SIZE,
        },
        maxPages: DEFAULT_INFINITE_MAX_PAGES,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
          const nextPageIndex = (lastPageParam.pageIndex ?? 0) + 1
          const totalPages = Math.ceil(
            lastPage.totalCount /
              (lastPageParam.pageSize ?? DEFAULT_INFINITE_PAGE_SIZE)
          )

          if (nextPageIndex >= totalPages) {
            return undefined
          }

          return {
            ...lastPageParam,
            pageIndex: nextPageIndex,
          }
        },
        getPreviousPageParam: (_firstPage, _allPages, firstPageParam) => {
          const prevPageIndex = (firstPageParam.pageIndex ?? 0) - 1

          if (prevPageIndex < 0) return undefined

          return {
            ...firstPageParam,
            pageIndex: prevPageIndex,
          }
        },
      },
      query: ({
        queryArg,
        pageParam: { pageIndex = 0, pageSize = DEFAULT_INFINITE_PAGE_SIZE },
      }) => {
        const params = paginationRequestSchema.parse({
          pageIndex,
          pageSize,
        })

        const search = queryArg?.search
        const status = queryArg?.status
        return {
          url: `${baseUri}/sessions`,
          method: 'GET',
          params: {
            ...params,
            orderBy: DEFAULT_LIST_ORDER,
            ...(search && { filterBy: { search } }),
            ...(status && { status }),
          },
        }
      },
    }),
  }),
  overrideExisting: false,
})

export const {
  useGetWorkflowSessionQuery,
  useListWorkflowSessionQuery,
  useListOrgWorkflowSessionsQuery,
  useCreateProjectSessionsMutation,
  useStopWorkflowSessionMutation,
  useGetAllSessionsInfiniteQuery,
} = workflowSessionApi
